import React, { useState, useEffect, useCallback } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import { ChevronRight, ChevronDown } from 'lucide-react';

interface Kamoku {
  kamoku_id: number;
  kamoku_code: string;
  kamoku_name: string;
  kamoku_yomi?: string | null; // Added field
  daikubun_id: number | null;
  daikubun_name?: string;
  is_active: boolean;
  shikin_kamoku_kubun_id?: number | null;
  jigyou_kamoku_kubun_id?: number | null;
  taisyaku_kamoku_kubun_id?: number | null;
  hojo_kamoku_ari?: boolean; // Added field
}

interface HojoKamoku {
  id?: number;
  hojo_code: string;
  hojo_name: string;
  shiyou_kubun: boolean;
  kamoku_id: number;
  tenant_id?: number;
}

interface ClassicKamokuManagementProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

const ClassicKamokuManagement: React.FC<any> = ({
  onMenuSelect,
  tenantName,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const [kamokuList, setKamokuList] = useState<Kamoku[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKamoku, setSelectedKamoku] = useState<Kamoku | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showHojoModal, setShowHojoModal] = useState(false);
  const [hojoList, setHojoList] = useState<HojoKamoku[]>([]);
  const [editingHojo, setEditingHojo] = useState<HojoKamoku | null>(null);
  const [inputCode, setInputCode] = useState('');
  const [inputName, setInputName] = useState('');
  const [hojoLoading, setHojoLoading] = useState(false);

  // CSVインポート用参照と状態
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  // ツリーテーブル用状態
  const [expandedKamokus, setExpandedKamokus] = useState<Record<number, boolean>>({});
  const [hojoDataMap, setHojoDataMap] = useState<Record<number, HojoKamoku[]>>({});

  // 勘定科目データの取得
  const fetchKamokuData = useCallback(async () => {
    if (currentTenantId == null) {
      setKamokuList([]);
      setSelectedKamoku(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiFetch('/api/kamoku');
      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '勘定科目データの取得に失敗しました');
        throw new Error(errorMessage);
      }
      const data = await response.json();
      const mapped: Kamoku[] = data.map((item: any) => ({
        kamoku_id: item.id,
        kamoku_code: item.kamoku_code,
        kamoku_name: item.kamoku,
        kamoku_yomi: item.kamoku_yomi, // Mapped field
        daikubun_id: item.daikubun_id,
        daikubun_name: item.daikubun,
        is_active: item.shiyou_kubun !== false,
        shikin_kamoku_kubun_id: item.shikin_kamoku_kubun_id,
        jigyou_kamoku_kubun_id: item.jigyou_kamoku_kubun_id,
        taisyaku_kamoku_kubun_id: item.taisyaku_kamoku_kubun_id,
        hojo_kamoku_ari: item.hojo_kamoku_ari, // Mapped field
      }));
      setKamokuList(mapped);
      setError(null);
    } catch (err: any) {
      setError(err?.message || '勘定科目データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, currentTenantId]);

  useEffect(() => {
    fetchKamokuData();
  }, [fetchKamokuData]);

  // 検索フィルター
  const filteredKamoku = kamokuList.filter(kamoku =>
    kamoku.kamoku_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kamoku.kamoku_code.includes(searchTerm) ||
    (kamoku.kamoku_yomi && kamoku.kamoku_yomi.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleKamokuSelect = (kamoku: Kamoku) => {
    setSelectedKamoku(kamoku);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!selectedKamoku) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/api/kamoku/${selectedKamoku.kamoku_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          kamoku_code: selectedKamoku.kamoku_code,
          kamoku: selectedKamoku.kamoku_name,
          kamoku_yomi: selectedKamoku.kamoku_yomi, // Added field
          daikubun_id: selectedKamoku.daikubun_id,
          shiyou_kubun: selectedKamoku.is_active,
          shikin_kamoku_kubun_id: selectedKamoku.shikin_kamoku_kubun_id,
          jigyou_kamoku_kubun_id: selectedKamoku.jigyou_kamoku_kubun_id,
          taisyaku_kamoku_kubun_id: selectedKamoku.taisyaku_kamoku_kubun_id,
          hojo_kamoku_ari: selectedKamoku.hojo_kamoku_ari,
        }),
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '勘定科目の更新に失敗しました');
        throw new Error(errorMessage);
      }

      // 成功: 編集モードを終了し、一覧を再取得
      setIsEditing(false);
      alert('勘定科目を更新しました');
      await fetchKamokuData();
    } catch (err: any) {
      setError(err?.message || '勘定科目の更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedKamoku(null);
  };

  // ワンクリック無効化・有効化トグル
  const handleToggleKamokuActive = async (kamoku: Kamoku) => {
    try {
      const response = await apiFetch(`/api/kamoku/${kamoku.kamoku_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shiyou_kubun: !kamoku.is_active }),
      });
      if (!response.ok) throw new Error('状態の更新に失敗しました');

      setKamokuList(prev => prev.map(k => k.kamoku_id === kamoku.kamoku_id ? { ...k, is_active: !k.is_active } : k));
      if (selectedKamoku?.kamoku_id === kamoku.kamoku_id) {
        setSelectedKamoku(prev => prev ? { ...prev, is_active: !prev.is_active } : prev);
      }
    } catch (err) {
      alert('更新に失敗しました');
    }
  };

  const handleToggleHojoActive = async (hojo: HojoKamoku, kamokuId: number) => {
    try {
      const response = await apiFetch(`/api/hojo-kamoku/${hojo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shiyou_kubun: !hojo.shiyou_kubun }),
      });
      if (!response.ok) throw new Error('状態の更新に失敗しました');

      setHojoDataMap(prev => {
        const list = prev[kamokuId];
        if (!list) return prev;
        return {
          ...prev,
          [kamokuId]: list.map(h => h.id === hojo.id ? { ...h, shiyou_kubun: !h.shiyou_kubun } : h)
        };
      });
    } catch (err) {
      alert('更新に失敗しました');
    }
  };

  // ツリー展開トグル
  const toggleExpandKamoku = async (kamokuId: number) => {
    setExpandedKamokus(prev => ({
      ...prev,
      [kamokuId]: !prev[kamokuId]
    }));

    if (!hojoDataMap[kamokuId]) {
      try {
        const response = await apiFetch(`/api/hojo-kamoku/by-kamoku/${kamokuId}`);
        if (response.ok) {
          const data = await response.json();
          setHojoDataMap(prev => ({ ...prev, [kamokuId]: data }));
        }
      } catch (err) {
        console.error("Failed to fetch hojo kamoku", err);
      }
    }
  };

  // 補助科目関連の処理
  const fetchHojoList = async (kamokuId: number) => {
    try {
      setHojoLoading(true);
      const response = await apiFetch(`/api/hojo-kamoku/by-kamoku/${kamokuId}`);
      if (!response.ok) {
        throw new Error('補助科目の取得に失敗しました');
      }
      const data = await response.json();
      setHojoList(data);
    } catch (err: any) {
      console.error(err);
      alert('補助科目の取得に失敗しました');
    } finally {
      setHojoLoading(false);
    }
  };

  const handleOpenHojoModal = () => {
    if (selectedKamoku) {
      fetchHojoList(selectedKamoku.kamoku_id);
      setShowHojoModal(true);
      setEditingHojo(null);
      setInputCode('');
      setInputName('');
    }
  };

  const handleSaveHojo = async () => {
    if (!inputCode || !inputName) {
      alert('コードと補助科目名は必須です');
      return;
    }

    try {
      setHojoLoading(true);
      const method = editingHojo ? 'PUT' : 'POST';
      const url = editingHojo ? `/api/hojo-kamoku/${editingHojo.id}` : '/api/hojo-kamoku/';

      const body: any = {
        hojo_code: inputCode,
        hojo_name: inputName,
        shiyou_kubun: true,
        kamoku_id: selectedKamoku?.kamoku_id,
        tenant_id: currentTenantId
      };

      if (editingHojo) {
        body.id = editingHojo.id;
      }

      const response = await apiFetch(url, {
        method,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const msg = await getErrorMessage(response, '補助科目の保存に失敗しました');
        throw new Error(msg);
      }

      await fetchHojoList(selectedKamoku!.kamoku_id);
      setEditingHojo(null);
      setInputCode('');
      setInputName('');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setHojoLoading(false);
    }
  };

  const handleEditHojoClick = (hojo: HojoKamoku) => {
    setEditingHojo(hojo);
    setInputCode(hojo.hojo_code);
    setInputName(hojo.hojo_name);
  };

  const handleCancelEditHojo = () => {
    setEditingHojo(null);
    setInputCode('');
    setInputName('');
  };

  const handleDeleteHojo = async (hojoId: number) => {
    if (!window.confirm('この補助科目を削除してもよろしいですか？')) return;

    try {
      setHojoLoading(true);
      const response = await apiFetch(`/api/hojo-kamoku/${hojoId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('補助科目の削除に失敗しました');
      }

      await fetchHojoList(selectedKamoku!.kamoku_id);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setHojoLoading(false);
    }
  };

  // CSVインポートボタンのクリック
  const handleImportButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // CSVファイルのアップロードとインポート処理
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiFetch('/api/kamoku_import', {
        method: 'POST',
        body: formData, // FormDataをそのまま渡す(Content-Typeはブラウザが設定)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'CSVインポートに失敗しました。');
      }

      const result = await response.json();
      const msg = `インポート完了!\n\n新規登録: ${result.success_count}件\n更新: ${result.update_count}件\nエラー: ${result.error_count}件`;
      alert(msg);

      if (result.errors && result.errors.length > 0) {
        console.warn("Import Errors:", result.errors);
        alert("一部の行でエラーが発生しました。コンソールをご確認ください。\n\n" + result.errors.slice(0, 5).join('\n') + (result.errors.length > 5 ? '\n...他' : ''));
      }

      // インポート完了後、一覧を再取得
      await fetchKamokuData();

    } catch (err: any) {
      console.error(err);
      alert(`エラー: ${err.message}`);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // ファイル入力をリセット
      }
    }
  };

  return (
    <div className="classic-kamoku-management" style={{
      backgroundColor: '#f0f0f0',
      minHeight: '100vh',
      fontFamily: "'MS UI Gothic', 'Hiragino Kaku Gothic ProN', sans-serif",
      fontSize: '11px'
    }}>
      {/* ヘッダー部分 */}
      <div className="classic-header" style={{
        backgroundColor: '#004499',
        color: 'white',
        padding: '8px 15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid #004499'
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            勘定科目マスタ管理
          </h1>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '4px' }}>
            {tenantName}
          </div>
        </div>
        <div style={{ fontSize: '10px', textAlign: 'right' }}>
          <div>System ver 1.33</div>
          <div>DB ver 3</div>
          <div style={{ marginTop: '4px', color: '#ffff99' }}>
            ユーザー: {currentUser?.username || 'admin'}
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={{ padding: '15px', display: 'flex', gap: '15px', height: 'calc(100vh - 80px)' }}>
        {/* 左側：勘定科目一覧 */}
        <div style={{ flex: '1', minWidth: '400px' }}>
          <fieldset style={{
            border: '2px inset #e0e0e0',
            backgroundColor: '#ffffff',
            padding: '10px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <legend style={{
              fontWeight: 'bold',
              fontSize: '12px',
              padding: '0 5px'
            }}>
              勘定科目一覧
            </legend>

            {/* 検索ボックスとインポートボタン */}
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <label style={{ fontSize: '11px', marginRight: '5px' }}>検索:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="科目名またはコードで検索"
                  style={{
                    padding: '2px 4px',
                    fontSize: '11px',
                    border: '1px inset #e0e0e0',
                    width: '200px'
                  }}
                />
              </div>

              {/* CSVインポート */}
              <div>
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <button
                  onClick={handleImportButtonClick}
                  disabled={isImporting}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    backgroundColor: '#e0e0e0',
                    border: '2px outset #e0e0e0',
                    cursor: isImporting ? 'not-allowed' : 'pointer',
                    opacity: isImporting ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  onMouseDown={(e) => !isImporting && (e.currentTarget.style.border = '2px inset #e0e0e0')}
                  onMouseUp={(e) => !isImporting && (e.currentTarget.style.border = '2px outset #e0e0e0')}
                  onMouseLeave={(e) => !isImporting && (e.currentTarget.style.border = '2px outset #e0e0e0')}
                >
                  {isImporting ? '⏳ 処理中...' : '📁 CSV一括登録'}
                </button>
              </div>
            </div>

            {/* データ表示エリア */}
            <div style={{ flex: 1, overflow: 'auto', border: '1px inset #e0e0e0' }}>
              {loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  データを読み込み中...
                </div>
              ) : error ? (
                <div style={{ padding: '20px', color: 'red' }}>
                  エラー: {error}
                </div>
              ) : (
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '11px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '30px' }}></th>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '70px' }}>コード</th>
                      <th style={{ border: '1px solid #999', padding: '4px' }}>勘定科目名</th>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '100px' }}>大区分</th>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '40px' }} title="事業活動収支科目区分">事業</th>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '40px' }} title="貸借対照表科目区分">貸借</th>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '40px' }} title="資金収支科目区分">資金</th>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '50px' }}>有効</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredKamoku.map((kamoku) => (
                      <React.Fragment key={kamoku.kamoku_id}>
                        <tr
                          onClick={() => handleKamokuSelect(kamoku)}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: selectedKamoku?.kamoku_id === kamoku.kamoku_id ? '#cce7ff' : 'white'
                          }}
                          onMouseOver={(e) => {
                            if (selectedKamoku?.kamoku_id !== kamoku.kamoku_id) {
                              e.currentTarget.style.backgroundColor = '#f0f8ff';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (selectedKamoku?.kamoku_id !== kamoku.kamoku_id) {
                              e.currentTarget.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleExpandKamoku(kamoku.kamoku_id); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
                            >
                              {expandedKamokus[kamoku.kamoku_id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                            {kamoku.kamoku_code}
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', fontWeight: 'bold' }}>
                            {kamoku.kamoku_name}
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                            {kamoku.daikubun_name || (kamoku.daikubun_id === 0 ? '未設定' : kamoku.daikubun_id)}
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                            {kamoku.jigyou_kamoku_kubun_id != null ? kamoku.jigyou_kamoku_kubun_id : '－'}
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                            {kamoku.taisyaku_kamoku_kubun_id != null ? kamoku.taisyaku_kamoku_kubun_id : '－'}
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                            {kamoku.shikin_kamoku_kubun_id != null ? kamoku.shikin_kamoku_kubun_id : '－'}
                          </td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={kamoku.is_active}
                              onChange={(e) => { e.stopPropagation(); handleToggleKamokuActive(kamoku); }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        </tr>

                        {/* 補助科目の展開表示 */}
                        {expandedKamokus[kamoku.kamoku_id] && !hojoDataMap[kamoku.kamoku_id] && (
                          <tr>
                            <td colSpan={8} style={{ border: '1px solid #ccc', textAlign: 'center', fontSize: '10px', color: '#999', padding: '4px' }}>
                              読み込み中...
                            </td>
                          </tr>
                        )}
                        {expandedKamokus[kamoku.kamoku_id] && hojoDataMap[kamoku.kamoku_id] && hojoDataMap[kamoku.kamoku_id].map(hojo => (
                          <tr key={`hojo-${hojo.id}`} style={{ backgroundColor: '#fdfdfd' }}>
                            <td style={{ border: '1px solid #ccc' }}></td>
                            <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center', color: '#666' }}>
                              {hojo.hojo_code}
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '4px 4px 4px 20px', color: '#444' }}>
                              └ {hojo.hojo_name}
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }} colSpan={4}>
                              <span style={{ fontSize: '10px', color: '#999' }}>補助科目</span>
                            </td>
                            <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={hojo.shiyou_kubun}
                                onChange={(e) => { e.stopPropagation(); handleToggleHojoActive(hojo, kamoku.kamoku_id); }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* 件数表示 */}
            <div style={{ marginTop: '5px', fontSize: '10px', color: '#666' }}>
              表示件数: {filteredKamoku.length} / 全{kamokuList.length}件
            </div>
          </fieldset>
        </div>

        {/* 右側：詳細・編集エリア */}
        <div style={{ flex: '0 0 300px' }}>
          <fieldset style={{
            border: '2px inset #e0e0e0',
            backgroundColor: '#ffffff',
            padding: '10px',
            height: '100%'
          }}>
            <legend style={{
              fontWeight: 'bold',
              fontSize: '12px',
              padding: '0 5px'
            }}>
              詳細情報
            </legend>

            {selectedKamoku ? (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                    勘定科目コード (保存時に5桁に自動調整されます):
                  </label>
                  <input
                    type="text"
                    value={selectedKamoku.kamoku_code}
                    readOnly={!isEditing}
                    onChange={(e) => setSelectedKamoku({ ...selectedKamoku, kamoku_code: e.target.value })}
                    onBlur={(e) => {
                      if (isEditing && e.target.value && /^\d+$/.test(e.target.value)) {
                        setSelectedKamoku({ ...selectedKamoku, kamoku_code: e.target.value.padStart(5, '0') });
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '11px',
                      border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                      backgroundColor: isEditing ? 'white' : '#f5f5f5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                    勘定科目名:
                  </label>
                  <input
                    type="text"
                    value={selectedKamoku.kamoku_name}
                    readOnly={!isEditing}
                    onChange={(e) => setSelectedKamoku({ ...selectedKamoku, kamoku_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '11px',
                      border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                      backgroundColor: isEditing ? 'white' : '#f5f5f5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                    科目よみ:
                  </label>
                  <input
                    type="text"
                    value={selectedKamoku.kamoku_yomi || ''}
                    readOnly={!isEditing}
                    onChange={(e) => setSelectedKamoku({ ...selectedKamoku, kamoku_yomi: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '11px',
                      border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                      backgroundColor: isEditing ? 'white' : '#f5f5f5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                    大区分ID:
                  </label>
                  <input
                    type="text"
                    value={(selectedKamoku.daikubun_id !== undefined && selectedKamoku.daikubun_id !== null && selectedKamoku.daikubun_id !== 0) ? selectedKamoku.daikubun_id : ''}
                    readOnly={!isEditing}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '11px',
                      border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                      backgroundColor: isEditing ? 'white' : '#f5f5f5'
                    }}
                  />
                </div>

                {/* 収支区分設定 */}
                <div style={{ marginBottom: '15px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', color: '#004499' }}>
                    収支区分設定
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                      資金収支科目区分ID:
                    </label>
                    <input
                      type="number"
                      value={selectedKamoku.shikin_kamoku_kubun_id ?? ''}
                      readOnly={!isEditing}
                      onChange={(e) => setSelectedKamoku({
                        ...selectedKamoku,
                        shikin_kamoku_kubun_id: e.target.value ? parseInt(e.target.value) : null
                      })}
                      style={{
                        width: '100%',
                        padding: '2px 4px',
                        fontSize: '11px',
                        border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                        backgroundColor: isEditing ? 'white' : '#f5f5f5'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                      事業活動収支科目区分ID:
                    </label>
                    <input
                      type="number"
                      value={selectedKamoku.jigyou_kamoku_kubun_id ?? ''}
                      readOnly={!isEditing}
                      onChange={(e) => setSelectedKamoku({
                        ...selectedKamoku,
                        jigyou_kamoku_kubun_id: e.target.value ? parseInt(e.target.value) : null
                      })}
                      style={{
                        width: '100%',
                        padding: '2px 4px',
                        fontSize: '11px',
                        border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                        backgroundColor: isEditing ? 'white' : '#f5f5f5'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                      貸借対照表科目区分ID:
                    </label>
                    <input
                      type="number"
                      value={selectedKamoku.taisyaku_kamoku_kubun_id ?? ''}
                      readOnly={!isEditing}
                      onChange={(e) => setSelectedKamoku({
                        ...selectedKamoku,
                        taisyaku_kamoku_kubun_id: e.target.value ? parseInt(e.target.value) : null
                      })}
                      style={{
                        width: '100%',
                        padding: '2px 4px',
                        fontSize: '11px',
                        border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                        backgroundColor: isEditing ? 'white' : '#f5f5f5'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedKamoku.is_active}
                      disabled={!isEditing}
                      onChange={(e) => setSelectedKamoku({ ...selectedKamoku, is_active: e.target.checked })}
                    />
                    有効
                  </label>
                </div>

                <div style={{ marginBottom: '20px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
                  <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedKamoku.hojo_kamoku_ari || false}
                      disabled={!isEditing}
                      onChange={(e) => setSelectedKamoku({ ...selectedKamoku, hojo_kamoku_ari: e.target.checked })}
                    />
                    補助科目あり
                  </label>

                  <button
                    onClick={handleOpenHojoModal}
                    disabled={!selectedKamoku.hojo_kamoku_ari || isEditing}
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: !selectedKamoku.hojo_kamoku_ari || isEditing ? '#f0f0f0' : '#4a90e2',
                      color: !selectedKamoku.hojo_kamoku_ari || isEditing ? '#999' : 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: !selectedKamoku.hojo_kamoku_ari || isEditing ? 'not-allowed' : 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    補助科目管理
                  </button>
                  {(!selectedKamoku.hojo_kamoku_ari) && (
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      ※補助科目を管理するには「補助科目あり」にチェックを入れて保存してください
                    </div>
                  )}
                </div>

                {/* ボタン */}
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {!isEditing ? (
                    <button
                      onClick={handleEdit}
                      style={{
                        backgroundColor: '#e0e0e0',
                        border: '2px outset #e0e0e0',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                      onMouseDown={(e) => e.currentTarget.style.border = '2px inset #e0e0e0'}
                      onMouseUp={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                      onMouseLeave={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                    >
                      編集
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        style={{
                          backgroundColor: '#e0e0e0',
                          border: '2px outset #e0e0e0',
                          padding: '4px 8px',
                          fontSize: '11px',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.6 : 1
                        }}
                        onMouseDown={(e) => !loading && (e.currentTarget.style.border = '2px inset #e0e0e0')}
                        onMouseUp={(e) => !loading && (e.currentTarget.style.border = '2px outset #e0e0e0')}
                        onMouseLeave={(e) => !loading && (e.currentTarget.style.border = '2px outset #e0e0e0')}
                      >
                        {loading ? '保存中...' : '保存'}
                      </button>
                      <button
                        onClick={handleCancel}
                        style={{
                          backgroundColor: '#e0e0e0',
                          border: '2px outset #e0e0e0',
                          padding: '4px 8px',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.border = '2px inset #e0e0e0'}
                        onMouseUp={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                        onMouseLeave={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                      >
                        キャンセル
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                勘定科目を選択してください
              </div>
            )}
          </fieldset>
        </div>
      </div>

      {/* 下部ボタン */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        left: '15px',
        right: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={() => onMenuSelect('main-menu')}
          style={{
            backgroundColor: '#e0e0e0',
            border: '2px outset #e0e0e0',
            padding: '6px 12px',
            fontSize: '11px',
            cursor: 'pointer',
            fontFamily: 'MS UI Gothic, sans-serif'
          }}
          onMouseDown={(e) => e.currentTarget.style.border = '2px inset #e0e0e0'}
          onMouseUp={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
          onMouseLeave={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
        >
          メインメニューに戻る
        </button>

        <div style={{ fontSize: '10px', color: '#666' }}>
          最終更新: {new Date().toLocaleString('ja-JP')}
        </div>
      </div>
      {/* 補助科目管理モーダル */}
      {showHojoModal && selectedKamoku && (
        <div className="modal-overlay" onClick={() => setShowHojoModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {selectedKamoku.kamoku_name} - 補助科目管理
              </h3>
              <button className="modal-close-button" onClick={() => setShowHojoModal(false)}>
                &times;
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="コード"
                  value={inputCode}
                  onChange={e => setInputCode(e.target.value)}
                  style={{ width: '80px', padding: '4px' }}
                />
                <input
                  type="text"
                  placeholder="補助科目名"
                  value={inputName}
                  onChange={e => setInputName(e.target.value)}
                  style={{ flex: 1, padding: '4px' }}
                />
                <button
                  onClick={handleSaveHojo}
                  disabled={hojoLoading}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {editingHojo ? '更新' : '追加'}
                </button>
                {editingHojo && (
                  <button
                    onClick={handleCancelEditHojo}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#999',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    キャンセル
                  </button>
                )}
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>コード</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>補助科目名</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {hojoList.map(hojo => (
                    <tr key={hojo.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px' }}>{hojo.hojo_code}</td>
                      <td style={{ padding: '8px' }}>{hojo.hojo_name}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <button
                          onClick={() => setEditingHojo(hojo)}
                          style={{ marginRight: '8px', cursor: 'pointer', background: 'none', border: 'none', color: '#2196F3' }}
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleDeleteHojo(hojo.id!)}
                          style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#F44336' }}
                        >
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                  {hojoList.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        補助科目が登録されていません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassicKamokuManagement;
